import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceStatus,
  IExperience,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { Button, DialogActions, DialogContent } from '@material-ui/core';
import { useMemo } from 'react';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../../components/AdditionalActionsMenu';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import {
  ID_TO_ROW_KEY,
  ItemSorters,
  RowsRenderer,
  ROW_HEIGHTS,
  useSortQueryParam
} from '../../../../../components/GroupableList';
import { Loader } from '../../../../../components/Loader';
import {
  TableToolbar,
  TableToolbarSection
} from '../../../../../components/Table';
import { IColumn } from '../../../../../components/Table/VirtualizedSortable';
import { useErrorLogger } from '../../../../../hooks/useErrorLogger';
import { Centered } from '../../../../../layout/Centered';
import { Section } from '../../../../../layout/Section';
import {
  getParticipantActor,
  toStandaloneAppUrl
} from '../../../../../services/experiences/instance';
import { removeDoc } from '../../../../../services/firebase';
import { formatDatePrecise, toMoment } from '../../../../../services/time';
import { JsonDialogInner } from '../../../../components/Json';
import { OrgNameLazy } from '../../../../components/OrgName';
import { useAllExperiences } from '../../../../services/experiences';
import { toFirestoreConsoleFromDoc } from '../../../../services/firebase';
import { publishTopic } from '../../../../services/pubsub';
import { ExperienceInstanceWithoutTemplate } from '../../components/ExperienceInstance';

type ColumnName =
  | 'orgId'
  | 'orgName'
  | 'tplId'
  | 'expId'
  | 'userId'
  | 'status'
  | 'startsAt'
  | 'actions';

type D = Doc<IExperience>;
type Column = IColumn<D, ColumnName>;

const Actions = ({ d }: { d: D }) => {
  const options: AdditionActionsMenuOption[] = useMemo(() => {
    return [
      {
        key: 'openInFirestore',
        label: 'Open in Firestore console',
        externalHref: toFirestoreConsoleFromDoc(d)
      },
      {
        key: 'openInStandaloneApp',
        label: 'Open in Standalone App',
        externalHref: toStandaloneAppUrl(d, PARTICIPANT_ACTOR_KEY)
      },
      {
        key: 'inspectRaw',
        label: 'Inspect (raw)...',
        dialog: ({ onClose }) => <JsonDialogInner onClose={onClose} data={d} />
      },
      {
        key: 'inspect',
        label: 'Inspect...',
        dialog: ({ onClose }) => (
          <>
            <DialogContent>
              <ExperienceInstanceWithoutTemplate exp={d} />
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
          </>
        )
      },
      {
        key: 'remove',
        label: 'Remove',
        onClick: () => removeDoc(d)
      }
    ];
  }, [d]);
  return <AdditionalActionsMenu options={options} />;
};

const COLUMNS: Column[] = [
  {
    key: 'orgId',
    head: () => 'Org ID',
    cell: (d) => d.data.orgId,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'orgName',
    head: () => 'Org Name',
    cell: (d) => <OrgNameLazy orgId={d.data.orgId} />,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 180,
    flexGrow: 0
  },
  {
    key: 'tplId',
    head: () => 'Template',
    cell: (d) => d.data.templateId,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'expId',
    head: () => 'Exp ID',
    cell: (d) => d.id,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'userId',
    head: () => 'User',
    cell: (d) => getParticipantActor(d)?.userId,
    align: 'left',
    sortable: false,
    defaultDirection: 'desc',
    width: 180,
    flexGrow: 1
  },
  {
    key: 'status',
    head: () => 'Status',
    cell: (d) => d.data.status,
    align: 'left',
    sortable: false,
    defaultDirection: 'desc',
    width: 100
  },
  {
    key: 'startsAt',
    head: () => 'startsAt',
    cell: (d) => formatDatePrecise(toMoment(d.data.startsAt)),
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 150,
    flexGrow: 0
  },
  {
    key: 'actions',
    head: () => '',
    alternateHead: () => 'Actions',
    cell: (d) => <Actions d={d} />,
    sortable: false,
    align: 'right',
    width: 50,
    flexGrow: 0
  }
];

const SORTERS: ItemSorters<D> = {
  startsAt: {
    key: 'startsAt',
    items: { sort: (d) => d.data.startsAt.toMillis(), dir: 'desc' }
  }
};

const DEFAULT_SORTER = SORTERS.startsAt;

export const PageExperiencesInstances = () => {
  return (
    <Section>
      <Centered>
        <i>Currently not available. Need to paginate first.</i>
      </Centered>
    </Section>
  );
};
export const PageExperiencesInstancesUnsafe = () => {
  const [ds, loading, error] = useAllExperiences();
  const [[sorter, dir], setSort] = useSortQueryParam('sort', SORTERS);

  useErrorLogger('experiences unsafe', error);
  console.log(ds);
  if (ds) {
    console.log(
      'late:',
      ds.filter((x) => x.data.status === ExperienceStatus.LATE).length
    );
  }

  return (
    <Section>
      <TableToolbar>
        <div />
        <TableToolbarSection>
          <ButtonWithPromise
            variant="contained"
            color="primary"
            pending="Starting check..."
            onClick={() =>
              publishTopic({
                topic: 'exp-experiences-checkForLateness',
                payload: { ts: Date.now() }
              })
            }
          >
            Check for lateness
          </ButtonWithPromise>
        </TableToolbarSection>
      </TableToolbar>
      {loading && <Loader height={500} />}
      {ds && (
        <RowsRenderer
          variant="contained"
          columns={COLUMNS}
          rows={ds}
          rowToKey={ID_TO_ROW_KEY}
          sorter={sorter || DEFAULT_SORTER}
          sortDirection={dir}
          renderHead={true}
          headProps={{ sticky: true }}
          onHeadClick={(c, d) => setSort([SORTERS[c.key] || DEFAULT_SORTER, d])}
          chunkSize={30}
          rootMargin="300px"
          rowHeight={ROW_HEIGHTS.dense}
          otherProps={undefined}
        />
      )}
    </Section>
  );
};
